import { createContext, useContext } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';

const ListTypeCtx = createContext<'ul' | 'ol'>('ul');

const components: Components = {
  // ── Block elements ────────────────────────────────────────────────────────

  p({ children }) {
    return <p className="text-sm leading-relaxed mb-2 last:mb-0">{children}</p>;
  },

  h2({ children }) {
    return (
      <h2 className="text-[11px] font-bold tracking-widest uppercase text-primary mt-6 mb-2 first:mt-0">
        {children}
      </h2>
    );
  },

  h3({ children }) {
    return (
      <h3 className="text-xs font-semibold tracking-wide uppercase text-muted-foreground mt-4 mb-1.5">
        {children}
      </h3>
    );
  },

  h4({ children }) {
    return (
      <h4 className="text-xs font-semibold text-foreground mt-3 mb-1">{children}</h4>
    );
  },

  ul({ children }) {
    return (
      <ListTypeCtx.Provider value="ul">
        <ul className="space-y-1 my-2">{children}</ul>
      </ListTypeCtx.Provider>
    );
  },

  ol({ children }) {
    return (
      <ListTypeCtx.Provider value="ol">
        <ol className="space-y-1 my-2 list-decimal pl-5">{children}</ol>
      </ListTypeCtx.Provider>
    );
  },

  // Falso positivo de react-hooks/rules-of-hooks: react-markdown
  // renderiza cada entrada de `components` COMO componente, así que useContext
  // acá corre durante un render legítimo. La regla no lo sabe porque el
  // nombre va en minúscula (tiene que coincidir con la etiqueta HTML).
  li({ children }) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const listType = useContext(ListTypeCtx);
    if (listType === 'ol') {
      return <li className="text-sm pl-1">{children}</li>;
    }
    return (
      <li className="flex gap-2 text-sm items-start">
        <span className="text-primary shrink-0 mt-0.5 leading-none">•</span>
        <span>{children}</span>
      </li>
    );
  },

  blockquote({ children }) {
    return (
      <blockquote className="bg-muted/30 border border-border/60 rounded-lg px-4 py-3 my-3 text-sm text-muted-foreground">
        {children}
      </blockquote>
    );
  },

  hr() {
    return <hr className="my-4 border-border/50" />;
  },

  // ── Table ─────────────────────────────────────────────────────────────────

  table({ children }) {
    return (
      <div className="my-3 border border-border rounded-lg overflow-hidden">
        <table className="w-full text-xs border-collapse">{children}</table>
      </div>
    );
  },

  thead({ children }) {
    return <thead className="bg-muted/40">{children}</thead>;
  },

  tbody({ children }) {
    return <tbody className="divide-y divide-border/50">{children}</tbody>;
  },

  tr({ children }) {
    return <tr className="hover:bg-muted/20 transition-colors">{children}</tr>;
  },

  th({ children }) {
    return (
      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {children}
      </th>
    );
  },

  td({ children }) {
    return (
      <td className="px-3 py-2 text-xs text-foreground/80 first:text-muted-foreground last:font-medium last:text-foreground">
        {children}
      </td>
    );
  },

  // ── Inline elements ───────────────────────────────────────────────────────

  strong({ children }) {
    return <strong className="font-semibold text-foreground">{children}</strong>;
  },

  em({ children }) {
    return <em className="italic text-muted-foreground">{children}</em>;
  },

  code({ children, className }) {
    const isBlock = className?.startsWith('language-');
    if (isBlock) {
      return (
        <pre className="bg-muted/50 border border-border/60 rounded-lg px-4 py-3 my-3 overflow-x-auto">
          <code className="text-[11px] font-mono text-foreground">{children}</code>
        </pre>
      );
    }
    return (
      <code className="text-primary bg-primary/10 px-1 py-0.5 rounded text-[11px] font-mono">
        {children}
      </code>
    );
  },

  a({ href, children }) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary underline underline-offset-2 hover:text-primary/80 transition-colors"
      >
        {children}
      </a>
    );
  },
};

interface BrainMarkdownProps {
  content: string;
}

export function BrainMarkdown({ content }: BrainMarkdownProps) {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {content}
    </ReactMarkdown>
  );
}
